import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { whatsappService } from "../../services/api";
import "../../styles/WhatsAppConfig.css";

function ConfiguracionWhatsApp() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [config, setConfig] = useState({
    activo: false,
    envio_automatico: false,
    hora_envio: "09:00",
    dias_anticipacion: 2,
    recordar_dia_pago: true,
    alertar_vencidos: true,
    confirmar_pagos: false,
    limite_mensajes_hora: 30,
  });

  const [numeroTest, setNumeroTest] = useState("");
  const [testLoading, setTestLoading] = useState(false);

  // Estados para WhatsApp QR
  const [qrCode, setQrCode] = useState("");
  const [whatsappStatus, setWhatsappStatus] = useState({
    connected: false,
    message: "Verificando...",
  });
  const [loadingQR, setLoadingQR] = useState(false);
  const [manuallyConnected, setManuallyConnected] = useState(() => {
    return localStorage.getItem('whatsapp_manual_connected') === 'true';
  });

  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    cargarConfiguracion();
    cargarEstadoWhatsApp();
  }, []);

  // Polling inteligente que respeta el estado manual
  useEffect(() => {
    if (manuallyConnected) {
      return; // No hacer polling si está conectado manualmente
    }

    const interval = setInterval(() => {
      cargarEstadoWhatsApp();
    }, 5000);

    return () => clearInterval(interval);
  }, [manuallyConnected]);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await whatsappService.getConfig();

      if (response.data) {
        setConfig(response.data);
      }
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      showMessage("error", "Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadoWhatsApp = async () => {
    // Si está manualmente conectado, no sobrescribir el estado
    if (manuallyConnected) {
      return;
    }

    try {
      const response = await whatsappService.getStatus();
      setWhatsappStatus(response.data);
    } catch (error) {
      setWhatsappStatus({
        connected: false,
        message: "Error al verificar estado",
      });
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      await whatsappService.updateConfig(config);
      showMessage("success", "✅ Configuración guardada correctamente");
    } catch (error) {
      showMessage("error", "Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerarQR = async () => {
    try {
      setLoadingQR(true);
      const response = await whatsappService.generateQR();

      if (response.data.qr) {
        setQrCode(response.data.qr);
        showMessage("success", "Código QR generado. Escanéalo con tu WhatsApp");
      } else {
        showMessage("error", "Error al generar código QR");
      }
    } catch (error) {
      showMessage(
        "error",
        "Error del servidor WhatsApp. Intenta con WhatsApp Web directo."
      );
    } finally {
      setLoadingQR(false);
    }
  };

  const handleReiniciarWhatsApp = async () => {
    try {
      setLoadingQR(true);
      setQrCode("");

      await whatsappService.restart();
      showMessage("success", "Reiniciando servidor WhatsApp...");

      // Esperar un poco antes de cargar el estado
      setTimeout(() => {
        cargarEstadoWhatsApp();
      }, 3000);
    } catch (error) {
      showMessage("error", "Error al reiniciar servidor");
    } finally {
      setLoadingQR(false);
    }
  };

  const handleTest = async () => {
    if (!numeroTest.trim()) {
      showMessage("error", "Ingresa un número de teléfono");
      return;
    }

    if (!whatsappStatus.connected && !manuallyConnected) {
      showMessage("error", "WhatsApp no está conectado");
      return;
    }

    try {
      setTestLoading(true);

      // Limpiar el número (remover espacios y caracteres especiales)
      let numeroLimpio = numeroTest.replace(/\s+/g, "").replace(/[^\d+]/g, "");

      // Si no empieza con +, agregar +1 (República Dominicana)
      if (!numeroLimpio.startsWith("+")) {
        numeroLimpio = "+1" + numeroLimpio;
      }

      const mensaje =
        "🧪 Este es un mensaje de prueba del sistema de préstamos. ¡Todo funciona correctamente!";

      // Crear URL de WhatsApp
      const whatsappUrl = `https://web.whatsapp.com/send?phone=${numeroLimpio}&text=${encodeURIComponent(
        mensaje
      )}`;

      // Abrir WhatsApp Web con foco automático
      const whatsappWindow = window.open(whatsappUrl, "_blank", "width=1200,height=800,scrollbars=yes,resizable=yes");

      // Intentar enfocar la ventana
      if (whatsappWindow) {
        whatsappWindow.focus();
      }

      showMessage(
        "success",
        "✅ WhatsApp Web abierto con mensaje preparado. Solo presiona ENTER para enviar el mensaje."
      );
      setNumeroTest("");
    } catch (error) {
      showMessage("error", "Error al abrir WhatsApp Web");
    } finally {
      setTestLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => {
      setMessage({ type: "", text: "" });
    }, 5000);
  };

  if (loading) {
    return (
      <div className="config-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="config-container">
      <div className="config-header">
        <h1>⚙️ Configuración WhatsApp</h1>
        <button
          onClick={() => navigate("/whatsapp")}
          className="btn btn-secondary"
        >
          ← Volver
        </button>
      </div>

      {message.text && (
        <div className={`message-alert ${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={handleGuardar} className="config-form">
        {/* Vinculación de WhatsApp */}
        <div className="config-section">
          <div className="section-header">
            <h2>📱 Vinculación de WhatsApp</h2>
            <p>Conecta tu cuenta de WhatsApp para enviar mensajes</p>
          </div>

          <div className="config-card">
            <div className="whatsapp-status">
              <div
                className={`status-badge ${
                  whatsappStatus.connected ? "connected" : "disconnected"
                }`}
              >
                {whatsappStatus.connected ? (
                  <>
                    <span className="status-icon">✅</span>
                    <span>WhatsApp Conectado</span>
                  </>
                ) : (
                  <>
                    <span className="status-icon">⚠️</span>
                    <span>WhatsApp Desconectado</span>
                  </>
                )}
              </div>
              <p className="status-message">{whatsappStatus.message}</p>
            </div>

            {!whatsappStatus.connected && (
              <div className="qr-container">
                <div className="connection-options">
                  {/* Opción Recomendada: WhatsApp Web Directo */}
                  <div className="option-card primary-option">
                    <h3>🌐 Opción Recomendada: WhatsApp Web Directo</h3>
                    <p>Método más confiable y sin problemas técnicos:</p>

                    <div className="web-instructions">
                      <h4>Pasos para vincular:</h4>
                      <ol>
                        <li>
                          <strong>Haz clic en "Abrir WhatsApp Web"</strong>{" "}
                          abajo
                        </li>
                        <li>
                          <strong>En tu teléfono</strong>: Ve a WhatsApp →
                          Configuración → Dispositivos vinculados
                        </li>
                        <li>
                          <strong>Toca "Vincular dispositivo"</strong>
                        </li>
                        <li>
                          <strong>Escanea el QR</strong> que aparece en la nueva
                          ventana
                        </li>
                        <li>
                          <strong>¡Listo!</strong> WhatsApp Web queda vinculado
                        </li>
                      </ol>
                    </div>

                    <div className="web-actions">
                      <button
                        type="button"
                        onClick={() =>
                          window.open(
                            "https://web.whatsapp.com",
                            "_blank",
                            "width=1200,height=800"
                          )
                        }
                        className="btn btn-success btn-large"
                      >
                        🌐 Abrir WhatsApp Web
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setWhatsappStatus({
                            connected: true,
                            message: "Conectado manualmente via WhatsApp Web",
                          });
                          setManuallyConnected(true);
                          localStorage.setItem('whatsapp_manual_connected', 'true');
                          showMessage(
                            "success",
                            "✅ WhatsApp marcado como conectado"
                          );
                        }}
                        className="btn btn-primary"
                      >
                        ✅ Marcar como Conectado
                      </button>
                    </div>

                    <div className="success-note">
                      <p>
                        💡 <strong>Ventaja:</strong> Esta opción usa WhatsApp
                        Web oficial y es 100% compatible
                      </p>
                    </div>
                  </div>

                  {/* Opción Alternativa: QR del Sistema */}
                  <div className="option-card secondary-option">
                    <h3>📱 Opción Alternativa: Código QR del Sistema</h3>
                    <p>
                      Si prefieres el método automático (puede tener problemas
                      técnicos):
                    </p>

                    {loadingQR ? (
                      <div className="qr-loading">
                        <div className="spinner"></div>
                        <p>Generando código QR...</p>
                      </div>
                    ) : qrCode ? (
                      <div className="qr-display">
                        <img
                          src={qrCode}
                          alt="Código QR WhatsApp"
                          className="qr-image"
                        />
                        <p>Escanea este código QR con tu WhatsApp</p>
                      </div>
                    ) : (
                      <div className="qr-actions">
                        <button
                          type="button"
                          onClick={handleGenerarQR}
                          className="btn btn-primary"
                        >
                          📱 Generar Código QR
                        </button>
                        <p>Genera un código QR para vincular tu WhatsApp</p>
                      </div>
                    )}

                    <div className="qr-controls">
                      <button
                        type="button"
                        onClick={handleReiniciarWhatsApp}
                        className="btn btn-warning btn-small"
                        disabled={loadingQR}
                      >
                        🚀 Reiniciar Servidor
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {whatsappStatus.connected && (
              <div className="whatsapp-connected-info">
                <p className="success-message">
                  ✅ Tu cuenta de WhatsApp está vinculada y lista para enviar
                  mensajes.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setWhatsappStatus({
                      connected: false,
                      message: "Desconectado manualmente",
                    });
                    setManuallyConnected(false);
                    localStorage.removeItem('whatsapp_manual_connected');
                    showMessage("info", "WhatsApp desconectado");
                  }}
                  className="btn btn-warning btn-small"
                >
                  🔌 Desconectar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Estado del Sistema */}
        <div className="config-section">
          <div className="section-header">
            <h2>📱 Estado del Sistema</h2>
          </div>

          <div className="config-card">
            <div className="switch-container switch-large">
              <input
                type="checkbox"
                id="activo"
                checked={config.activo}
                onChange={(e) =>
                  setConfig({ ...config, activo: e.target.checked })
                }
              />
              <div className="switch-slider"></div>
              <label htmlFor="activo" className="switch-label">
                <strong>Activar WhatsApp</strong>
                <small>
                  {config.activo
                    ? "✅ Sistema activo - Los mensajes se enviarán normalmente"
                    : "⚠️ Sistema desactivado - No se enviarán mensajes"}
                </small>
              </label>
            </div>

            {!config.activo && (
              <div className="warning-box">
                ⚠️ El sistema está desactivado. Actívalo para comenzar a enviar
                mensajes.
              </div>
            )}
          </div>
        </div>

        {/* Envío Automático */}
        <div className="config-section">
          <div className="section-header">
            <h2>🤖 Envío Automático</h2>
            <p>Configura el envío automático de recordatorios</p>
          </div>

          <div className="config-card">
            <div className="switch-container">
              <input
                type="checkbox"
                id="envio_automatico"
                checked={config.envio_automatico}
                onChange={(e) =>
                  setConfig({ ...config, envio_automatico: e.target.checked })
                }
              />
              <div className="switch-slider"></div>
              <label htmlFor="envio_automatico" className="switch-label">
                Envío automático diario
              </label>
            </div>

            {config.envio_automatico && (
              <>
                <div className="form-group">
                  <label>Hora de Envío</label>
                  <input
                    type="time"
                    value={config.hora_envio}
                    onChange={(e) =>
                      setConfig({ ...config, hora_envio: e.target.value })
                    }
                  />
                  <small>
                    Los mensajes automáticos se enviarán a esta hora todos los
                    días
                  </small>
                </div>

                <div className="form-group">
                  <label>Días de Anticipación</label>
                  <input
                    type="number"
                    min="0"
                    max="7"
                    value={config.dias_anticipacion}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        dias_anticipacion: parseInt(e.target.value),
                      })
                    }
                  />
                  <small>
                    Enviar recordatorio X días antes del vencimiento
                  </small>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tipos de Mensajes */}
        <div className="config-section">
          <div className="section-header">
            <h2>📝 Tipos de Mensajes</h2>
            <p>Configura qué tipos de mensajes enviar automáticamente</p>
          </div>

          <div className="config-card">
            <div className="checkbox-grid">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="recordar_dia_pago"
                  checked={config.recordar_dia_pago}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      recordar_dia_pago: e.target.checked,
                    })
                  }
                />
                <label htmlFor="recordar_dia_pago">
                  <strong>📅 Recordatorios de Pago</strong>
                  <small>Recordar fechas de pago próximas</small>
                </label>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="alertar_vencidos"
                  checked={config.alertar_vencidos}
                  onChange={(e) =>
                    setConfig({ ...config, alertar_vencidos: e.target.checked })
                  }
                />
                <label htmlFor="alertar_vencidos">
                  <strong>⚠️ Alertas de Vencimiento</strong>
                  <small>Avisar sobre pagos vencidos</small>
                </label>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="confirmar_pagos"
                  checked={config.confirmar_pagos}
                  onChange={(e) =>
                    setConfig({ ...config, confirmar_pagos: e.target.checked })
                  }
                />
                <label htmlFor="confirmar_pagos">
                  <strong>✅ Confirmaciones de Pago</strong>
                  <small>Confirmar cuando se reciba un pago</small>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Límites y Control */}
        <div className="config-section">
          <div className="section-header">
            <h2>🚦 Límites y Control</h2>
            <p>Configura límites para evitar spam</p>
          </div>

          <div className="config-card">
            <div className="form-group">
              <label>Límite de Mensajes por Hora</label>
              <input
                type="number"
                min="1"
                max="100"
                value={config.limite_mensajes_hora}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    limite_mensajes_hora: parseInt(e.target.value),
                  })
                }
              />
              <small>
                Máximo número de mensajes automáticos por hora (recomendado: 30)
              </small>
            </div>
          </div>
        </div>

        {/* Pruebas */}
        <div className="config-section">
          <div className="section-header">
            <h2>🧪 Pruebas del Sistema</h2>
            <p>Envía un mensaje de prueba para verificar que todo funciona</p>
          </div>

          <div className="config-card">
            <div className="test-section">
              {/* Debug info */}
                            <div
                style={{
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "10px",
                  marginBottom: "10px",
                  fontSize: "12px",
                }}
              >
                <strong>Estado:</strong>
                {whatsappStatus.connected || manuallyConnected ? (
                  <span style={{color: 'green'}}> ✅ WhatsApp Conectado</span>
                ) : (
                  <span style={{color: 'red'}}> ❌ WhatsApp Desconectado</span>
                )}
                {manuallyConnected && <span style={{color: 'blue'}}> (Manual)</span>}
              </div>
              <div className="form-group">
                <label>Número de Prueba (con código de país)</label>
                <input
                  type="tel"
                  placeholder="Ej: +1829XXXXXXX"
                  value={numeroTest}
                  onChange={(e) => setNumeroTest(e.target.value)}
                />
                <small>
                  Incluye el código de país (Ej: +1809, +1829, +1849)
                </small>
              </div>{" "}
              <button
                type="button"
                onClick={handleTest}
                disabled={
                  testLoading ||
                  (!whatsappStatus.connected && !manuallyConnected)
                }
                className="btn btn-info"
                title={`Debug: connected=${whatsappStatus.connected}, manual=${manuallyConnected}, loading=${testLoading}`}
              >
                {testLoading ? (
                  <>
                    <div className="spinner-small"></div>
                    Enviando...
                  </>
                ) : (
                  "🧪 Enviar Mensaje de Prueba"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary btn-large"
          >
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Guardando...
              </>
            ) : (
              "💾 Guardar Configuración"
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate("/whatsapp")}
            className="btn btn-secondary"
          >
            ❌ Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

export default ConfiguracionWhatsApp;
